
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Package, AlertTriangle, Users, Plus, Clock, Search,
  LogIn, Wrench, CheckCircle2, ArrowRight, Loader2, LogOut
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCondominiumStats } from "@/hooks/useCondominiumStats";
import { useVisitors, useUpdateVisitorExit } from "@/hooks/useVisitors";
import { usePackages, useUpdatePackageStatus } from "@/hooks/usePackages";
import { useNavigate } from "react-router-dom";
import RegistrarEncomendaModal from "../RegistrarEncomendaModal";
import NovaOcorrenciaModal from "../NovaOcorrenciaModal";
import RegistrarVisitanteModal from "../RegistrarVisitanteModal";
import { useCondominium } from "@/hooks/useCondominium";
import { useCalculateEmployeeHours } from "@/hooks/useEmployeeHoursBank";
import { formatHoursBalance } from "@/lib/utils";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";

const AVATAR_COLORS = [
  "bg-purple-500", "bg-blue-500", "bg-green-500", "bg-rose-500",
  "bg-amber-500", "bg-cyan-500", "bg-indigo-500", "bg-teal-500"
];

const getInitials = (name: string) => {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const relativeTime = (dateStr: string) => {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: false, locale: ptBR });
  } catch { return ""; }
};

const DashboardPorteiro = () => {
  const { profile } = useAuth();
  const { data: stats, isLoading } = useCondominiumStats();
  const { data: visitors = [] } = useVisitors();
  const { data: packages = [] } = usePackages();
  const { data: condominium } = useCondominium();
  const { data: hoursData } = useCalculateEmployeeHours(profile?.id, profile?.condominium_id);
  const navigate = useNavigate();
  const exitMutation = useUpdateVisitorExit();
  const updatePackageMutation = useUpdatePackageStatus();

  const firstName = profile?.full_name?.split(" ")[0] || "Porteiro";

  // Visitantes de hoje
  const todayStr = new Date().toDateString();
  const todayVisitors = visitors.filter((v) => {
    const d = new Date(v.visit_date || v.created_at).toDateString();
    return d === todayStr;
  });

  // Visitantes dentro do condomínio
  const insideVisitors = visitors.filter(
    (v) => v.entry_time && !v.exit_time && v.status === "approved"
  );

  // Encomendas pendentes
  const pendingPackages = (packages as any[]).filter((p) => p.status === "pending");

  // Últimos registros (combinar visitantes recentes + encomendas)
  const recentEvents = [
    ...visitors.slice(0, 10).map((v) => ({
      time: v.entry_time || v.created_at,
      type: v.exit_time ? "saida" : "entrada",
      unit: v.unit?.number ? `${v.unit.building ? v.unit.building + " - " : ""}${v.unit.number}` : "-",
      details: v.name || "Visitante",
      porteiro: v.authorized_by_user?.full_name?.split(" ")[0] || "-",
    })),
    ...pendingPackages.slice(0, 5).map((p: any) => ({
      time: p.received_at || p.created_at,
      type: "encomenda",
      unit: p.unit?.number ? `${p.unit.building ? p.unit.building + " - " : ""}${p.unit.number}` : "-",
      details: p.sender || p.recipient_name || "Encomenda",
      porteiro: p.received_by_user?.full_name?.split(" ")[0] || "-",
    })),
  ]
    .filter((e) => e.time)
    .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
    .slice(0, 8);

  const eventBadge = (type: string) => {
    switch (type) {
      case "entrada":
        return <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] font-semibold">Entrada</Badge>;
      case "saida":
        return <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px] font-semibold">Saída</Badge>;
      case "encomenda":
        return <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] font-semibold">Encomenda</Badge>;
      default:
        return <Badge variant="outline" className="text-[10px]">{type}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header com saudação e badge de horas */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground">
            Olá, {firstName}
          </h1>
          <p className="text-xs text-muted-foreground">
            {condominium?.name || "Condomínio"} — Portaria
          </p>
        </div>
        {hoursData && (
          <Badge
            variant={hoursData.balance >= 0 ? "default" : "destructive"}
            className="text-sm font-mono px-3 py-1.5"
          >
            <Clock className="h-3 w-3 mr-1.5" />
            {formatHoursBalance(hoursData.balance)}
          </Badge>
        )}
      </div>

      {/* Barra de busca + ações rápidas */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar unidade, morador ou placa..."
            className="pl-9 rounded-full bg-muted/50 border-0 h-9 text-sm"
            readOnly
            onClick={() => navigate("/visitantes")}
          />
        </div>
        <div className="flex gap-2 shrink-0">
          <RegistrarVisitanteModal>
            <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white h-9">
              <LogIn className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs">Nova Entrada</span>
            </Button>
          </RegistrarVisitanteModal>
          <RegistrarEncomendaModal>
            <Button size="sm" variant="outline" className="h-9">
              <Package className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs">Encomenda</span>
            </Button>
          </RegistrarEncomendaModal>
          <NovaOcorrenciaModal>
            <Button size="sm" variant="outline" className="h-9">
              <AlertTriangle className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs">Ocorrência</span>
            </Button>
          </NovaOcorrenciaModal>
        </div>
      </div>

      {/* 3 Cards lado a lado */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Card Visitantes */}
        <Card className="rounded-xl">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-sky-500" />
              <CardTitle className="text-sm font-semibold">Visitantes</CardTitle>
            </div>
            <span className="text-[11px] text-muted-foreground">
              {todayVisitors.length} hoje
            </span>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {todayVisitors.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                Nenhum visitante hoje
              </p>
            ) : (
              <div className="space-y-2.5 max-h-48 overflow-y-auto">
                {todayVisitors.slice(0, 5).map((v) => (
                  <div key={v.id} className="flex items-center gap-2.5">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback
                        className={`${getAvatarColor(v.name || "V")} text-white text-[10px] font-bold`}
                      >
                        {getInitials(v.name || "V")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{v.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {v.unit?.number ? `Unid. ${v.unit.number}` : v.document || v.notes || "—"}
                      </p>
                    </div>
                    {v.entry_time && !v.exit_time ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    ) : null}
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 text-xs text-sky-600 hover:text-sky-700 h-7"
              onClick={() => navigate("/visitantes")}
            >
              Ver todos <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Card Encomendas */}
        <Card className="rounded-xl">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-amber-500" />
              <CardTitle className="text-sm font-semibold">Encomendas</CardTitle>
            </div>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {pendingPackages.length} para retirar
            </Badge>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {pendingPackages.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                Nenhuma encomenda pendente
              </p>
            ) : (
              <div className="space-y-2.5 max-h-48 overflow-y-auto">
                {pendingPackages.slice(0, 5).map((p: any) => (
                  <div key={p.id} className="flex items-center gap-2.5">
                    <div className="h-7 w-7 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                      <Package className="h-3.5 w-3.5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">
                        {p.sender || p.recipient_name}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {p.unit?.number ? `Unid. ${p.unit.number}` : "—"}
                        {p.received_at ? ` · ${relativeTime(p.received_at)}` : ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 shrink-0"
                      disabled={updatePackageMutation.isPending}
                      onClick={() => updatePackageMutation.mutate({
                        id: p.id,
                        status: "delivered",
                        delivered_at: new Date().toISOString()
                      })}
                    >
                      {updatePackageMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 className="h-3 w-3" />
                          <span className="hidden sm:inline text-[10px]">Entregar</span>
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 text-xs text-amber-600 hover:text-amber-700 h-7"
              onClick={() => navigate("/encomendas")}
            >
              Ver todas <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </CardContent>
        </Card>

        {/* Card Prestadores / No condomínio */}
        <Card className="rounded-xl">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-orange-500" />
              <CardTitle className="text-sm font-semibold">No condomínio</CardTitle>
            </div>
            <span className="text-[11px] text-muted-foreground">
              {insideVisitors.length} pessoas
            </span>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {insideVisitors.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                Ninguém no momento
              </p>
            ) : (
              <div className="space-y-2.5 max-h-48 overflow-y-auto">
                {insideVisitors.slice(0, 5).map((v) => {
                  const isProvider = v.visitor_type === "delivery" || v.visitor_type === "service";
                  return (
                    <div key={v.id} className="flex items-center gap-2.5">
                      <Avatar className="h-7 w-7">
                        <AvatarFallback
                          className={`${getAvatarColor(v.name || "P")} text-white text-[10px] font-bold`}
                        >
                          {getInitials(v.name || "P")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{v.name}</p>
                        <div className="flex items-center gap-1.5">
                          {isProvider && (
                            <Badge className="bg-orange-100 text-orange-700 border-0 text-[9px] px-1 py-0 font-semibold">
                              {v.visitor_type === "delivery" ? "ENTREGA" : "SERVIÇO"}
                            </Badge>
                          )}
                          <span className="text-[10px] text-muted-foreground">
                            {v.entry_time
                              ? format(new Date(v.entry_time), "HH:mm", { locale: ptBR })
                              : ""}
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50 shrink-0"
                        disabled={exitMutation.isPending}
                        onClick={() => exitMutation.mutate(v.id)}
                      >
                        {exitMutation.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <LogOut className="h-3 w-3" />
                            <span className="hidden sm:inline text-[10px]">Saída</span>
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 text-xs text-orange-600 hover:text-orange-700 h-7"
              onClick={() => navigate("/visitantes")}
            >
              Ver todos <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Últimos Registros */}
      <Card className="rounded-xl">
        <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold">Últimos Registros</CardTitle>
          <Button
            variant="link"
            size="sm"
            className="text-xs text-muted-foreground p-0 h-auto"
            onClick={() => navigate("/visitantes")}
          >
            Ver histórico completo
          </Button>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {recentEvents.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              Nenhum registro recente
            </p>
          ) : (
            <div className="overflow-x-auto -mx-4">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider h-8 pl-4">Horário</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider h-8">Evento</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider h-8">Unidade</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider h-8 hidden sm:table-cell">Detalhes</TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider h-8 pr-4 hidden md:table-cell">Porteiro</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentEvents.map((event, i) => (
                    <TableRow key={i} className="hover:bg-muted/30">
                      <TableCell className="text-xs py-2 pl-4 font-mono text-muted-foreground">
                        {event.time
                          ? format(new Date(event.time), "HH:mm", { locale: ptBR })
                          : "—"}
                      </TableCell>
                      <TableCell className="py-2">{eventBadge(event.type)}</TableCell>
                      <TableCell className="text-xs py-2">{event.unit}</TableCell>
                      <TableCell className="text-xs py-2 hidden sm:table-cell truncate max-w-[120px]">
                        {event.details}
                      </TableCell>
                      <TableCell className="text-xs py-2 pr-4 hidden md:table-cell text-muted-foreground">
                        {event.porteiro}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPorteiro;
